
import React, { useEffect, useState } from 'react';
import { useBuilderState } from '@/components/builder/useBuilderState';
import MobileBuilderLayout from '@/components/builder/MobileBuilderLayout';
import DesktopBuilderLayout from '@/components/builder/DesktopBuilderLayout';
import EmailPreview from '@/components/EmailPreview';
import { useIsMobile } from "@/hooks/use-mobile";
import { useParams } from 'react-router-dom';
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from "@/components/ui/resizable";
import { useToast } from "@/hooks/use-toast";
import { useEmailTemplateGenerator } from '@/hooks/useEmailTemplateGenerator';
import TemplateHistory from '@/components/TemplateHistory';
import '../styles/email-preview.css';
import '../styles/rich-message.css';

const Builder: React.FC = () => {
  const isMobile = useIsMobile();
  const params = useParams();
  const { toast } = useToast();
  const [showHistory, setShowHistory] = useState<boolean>(false);
  
  const builderState = useBuilderState();
  const {
    view,
    setView,
    emailContent,
    isGenerating,
    setIsGenerating,
    initialPrompt,
    isInsuranceOffer,
    isInsuranceComparison,
    analysisResult,
    activePanel,
    setActivePanel,
    containerRef,
    isOfferModalOpen,
    setIsOfferModalOpen,
    offerUrl,
    offerTitle,
    detectedLanguage,
    currentChatId,
    isStreaming,
    streamingCode,
    handleEmailGenerated,
    handleStreamingCodeUpdate,
    handleStreamingComplete,
    clearPreview,
    user
  } = builderState;
  
  // Use our improved template generator hook with proper completion handling
  const templateGenerator = useEmailTemplateGenerator({
    onSuccess: (content) => {
      handleEmailGenerated(content);
      setIsGenerating(false); // Ensure we reset the loading state
    },
    onGenerating: setIsGenerating,
    onStreamUpdate: (html) => {
      handleStreamingCodeUpdate(html);
    },
    onStreamComplete: (finalHtml) => {
      handleStreamingComplete(finalHtml);
      setIsGenerating(false); // Ensure we reset the loading state when streaming completes
    }
  });
  
  // Set dark background for builder view
  useEffect(() => {
    document.body.classList.add('bg-black');
    return () => {
      document.body.classList.remove('bg-black');
    };
  }, []);

  // Set viewport meta for mobile
  useEffect(() => {
    const viewportMeta = document.querySelector('meta[name="viewport"]');
    if (viewportMeta) {
      viewportMeta.setAttribute('content', 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'viewport';
      meta.content = 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no';
      document.head.appendChild(meta);
    }
  }, []);
  
  // Handle chat ID changes from URL
  useEffect(() => {
    if (params?.chatId && params.chatId !== currentChatId) {
      console.log("Builder: URL param chatId changed to:", params.chatId, "Current:", currentChatId);
      localStorage.setItem('currentChatId', params.chatId);
      
      window.dispatchEvent(new CustomEvent('open-chat', { 
        detail: { chatId: params.chatId },
        bubbles: true 
      }));
      
      window.dispatchEvent(new CustomEvent('refresh-chat-list'));
    }
  }, [params?.chatId, currentChatId]);

  // Apply premium chat styling
  useEffect(() => {
    const chatContainer = document.querySelector('.chat-container');
    if (chatContainer) {
      chatContainer.classList.add('premium-chat');
    }
    
    if (!localStorage.getItem('preferredLanguage')) {
      localStorage.setItem('preferredLanguage', 'de');
    }
  }, []);

  // Handle template selection from history
  const handleTemplateSelect = (content: string) => {
    handleEmailGenerated(content);
    if (isMobile) {
      setShowHistory(false);
    }
  };

  console.log("Builder rendering with chatId:", params?.chatId, "Current chatId:", currentChatId);
  
  return (
    <div className="min-h-screen w-full bg-black border-l border-gray-900 overflow-hidden">
      {isMobile ? (
        <MobileBuilderLayout
          activePanel={activePanel as "chat" | "preview"}
          setActivePanel={(panel: any) => setActivePanel(panel)}
          view={view}
          setView={setView}
          emailContent={emailContent}
          isGenerating={isGenerating}
          isStreaming={isStreaming}
          streamingCode={streamingCode}
          isInsuranceOffer={isInsuranceOffer}
          isInsuranceComparison={isInsuranceComparison}
          analysisResult={analysisResult}
          detectedLanguage={detectedLanguage || 'de'}
          initialPrompt={initialPrompt}
          handleEmailGenerated={handleEmailGenerated}
          handleStreamingCodeUpdate={handleStreamingCodeUpdate}
          handleStreamingComplete={handleStreamingComplete}
          currentChatId={currentChatId}
          isOfferModalOpen={isOfferModalOpen}
          setIsOfferModalOpen={setIsOfferModalOpen}
          offerUrl={offerUrl}
          offerTitle={offerTitle}
          userEmail={user?.email}
          containerRef={containerRef}
          setIsGenerating={setIsGenerating}
          triggerTemplateGeneration={templateGenerator.generateTemplate}
          showHistory={showHistory}
          setShowHistory={setShowHistory}
          onTemplateSelect={handleTemplateSelect}
        />
      ) : (
        <ResizablePanelGroup direction="horizontal" className="min-h-screen">
          <ResizablePanel defaultSize={50} minSize={30} className="bg-black">
            <DesktopBuilderLayout
              view={view}
              setView={setView}
              emailContent={emailContent}
              isGenerating={isGenerating}
              isStreaming={isStreaming}
              streamingCode={streamingCode}
              isInsuranceOffer={isInsuranceOffer}
              isInsuranceComparison={isInsuranceComparison}
              analysisResult={analysisResult}
              detectedLanguage={detectedLanguage || 'de'}
              initialPrompt={initialPrompt}
              handleEmailGenerated={handleEmailGenerated}
              handleStreamingCodeUpdate={handleStreamingCodeUpdate}
              handleStreamingComplete={handleStreamingComplete}
              currentChatId={currentChatId}
              isOfferModalOpen={isOfferModalOpen}
              setIsOfferModalOpen={setIsOfferModalOpen}
              offerUrl={offerUrl}
              offerTitle={offerTitle}
              userEmail={user?.email}
              containerRef={containerRef}
              setIsGenerating={setIsGenerating}
              triggerTemplateGeneration={templateGenerator.generateTemplate}
            >
              {/* Add Template History panel */}
              {user && <TemplateHistory onSelect={handleTemplateSelect} />}
            </DesktopBuilderLayout>
          </ResizablePanel>
          <ResizableHandle withHandle />
          <ResizablePanel defaultSize={50} minSize={30} className="bg-black">
            <EmailPreview
              view={view}
              onViewChange={setView}
              emailContent={emailContent}
              isGenerating={isGenerating}
              isStreaming={isStreaming}
              streamingCode={streamingCode}
              isInsuranceOffer={isInsuranceOffer}
              isInsuranceComparison={isInsuranceComparison}
              analysisResult={analysisResult}
              language={detectedLanguage}
              initialPrompt={initialPrompt || undefined}
            />
          </ResizablePanel>
        </ResizablePanelGroup>
      )}
    </div>
  );
};

export default Builder;
